'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useCallOnce } from '@/ui/hooks/use-call-once';
import { DocShapeId } from '@/utils/shapeIds';
import { useCartStore } from '@/ui/store/cart';
import { apiPaths } from '@/constants/apiPaths';
import { Smartlock } from '@/ui/services/SmartlockService';
import { Subscription } from '@/ui/services/SubscriptionService';
import { DigitalProduct } from '@/ui/services/DigitalProductService';
import { Accessory } from '@/ui/services/AccessoryService';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { urlParameterCodes } from '@/constants/urlParams';
import { getUrlParamValue } from '@/ui/utils/client-url-helpers';
import { nukiIdValidation } from '@/ui/utils/nuki-id-validation';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { getExperimentTrackingProps } from '@/utils/experiment-helper';
import { MathHelper } from '@/utils/math-helper';
import { SmartlockDetailOverlay } from '../../SmartlockDetailOverlay';
import { AccessoryDetailOverlay } from '../../AccessoryDetailOverlay';
import { NukiIdOverlay } from '../../NukiIdOverlay';
import { CrystallizeProductData } from '../../types/CrystallizeProductData';
import { SmartlockCrystallizeData } from '../../SmartlockDetail/types/SmartlockCrystallizeData';
import { AccessoryCrystallizeData } from '../../AccessoryDetail/types/AccessoryCrystallizeData';
import { SubscriptionCrystallizeData } from '../../types/SubscriptionCrystallizeData';
import { DigitalProductCrystallizeData } from '../../types/DigitalProductCrystallizeData';

export interface ProductDetailOverlayProps {
  data: CrystallizeProductData;
  initialChooserOpen?: boolean;
}

export function ProductDetailOverlay({
  data,
  initialChooserOpen,
}: Readonly<ProductDetailOverlayProps>) {
  const cartStore = useCartStore();
  const local = useLocalization();

  const [nukiIds, setNukiIds] = useState<string[]>([]);

  // useRef is necessary here because track() is called during render
  // Using useState would cause infinite re-renders
  const trackingDataRef = useRef<{
    product_id: string;
    product_title: string;
    product_price: number | undefined;
    local_currency: string | undefined;
  } | null>(null);

  // GA4 view_item - wrapped in useCallOnce to fire only once per component lifecycle
  const trackViewItemOnce = useCallOnce(() => {
    if (!trackingDataRef.current) {
      return;
    }

    const d = trackingDataRef.current;
    const roundedPrice =
      d.product_price === undefined ? undefined : MathHelper.round(d.product_price);
    DataLayerHelper.pushViewItem(
      [
        {
          item_id: d.product_id,
          item_name: d.product_title,
          item_brand: 'Nuki',
          price: roundedPrice,
          quantity: 1,
        },
      ],
      d.local_currency,
      roundedPrice,
    );
  });

  const track = (
    product_title: string,
    product_id: string,
    product_price: number | undefined,
    stock_level: number | undefined,
    variant_id: string | undefined,
    local_currency: string | undefined,
    language: string | undefined,
  ) => {
    // Store data for GA4 view_item (fired in useEffect below to ensure URL params are available)
    trackingDataRef.current = {
      product_id,
      product_title,
      product_price,
      local_currency,
    };

    // Exponea/Bloomreach tracking
    if (globalThis.window?.exponea) {
      globalThis.window.exponea.track('view_item', {
        title: product_title,
        product_id,
        price: product_price,
        stock_level,
        variant_id,
        local_currency,
        language,
        ...getExperimentTrackingProps(),
      });
    }
  };

  // Fire GA4 view_item after mount; skip if bundle is selected (handled by bundleProvider)
  useEffect(() => {
    const hasBundleSelected = !!getUrlParamValue(urlParameterCodes.productDetail.bundleId);
    if (!hasBundleSelected) {
      trackViewItemOnce();
    }
  }, [trackViewItemOnce]);

  if (data.shape.identifier === DocShapeId.smartLock) {
    const productInstance = new Smartlock(data as SmartlockCrystallizeData, cartStore);
    track(
      productInstance.name,
      productInstance.variants[0]?.sku, // product_id should be sku -> that's ok
      productInstance.variants[0]?.priceVariant?.price,
      productInstance.variants[0]?.stockCount || undefined,
      productInstance.id, // variant_id should be unique product id -> that's ok
      productInstance.variants[0]?.priceVariant?.currency,
      local.language,
    );
    return (
      <SmartlockDetailOverlay product={productInstance} initialChooserOpen={initialChooserOpen} />
    );
  }
  if (data.shape.identifier === DocShapeId.accessory) {
    const productInstance = new Accessory(data as AccessoryCrystallizeData, cartStore);
    track(
      productInstance.name,
      productInstance.variants[0]?.sku, // product_id should be sku -> that's ok
      productInstance.variants[0]?.priceVariant?.price,
      productInstance.variants[0]?.stockCount || undefined,
      productInstance.id, // variant_id should be unique product id -> that's ok
      productInstance.variants[0]?.priceVariant?.currency,
      local.language,
    );
    return (
      <AccessoryDetailOverlay product={productInstance} initialChooserOpen={initialChooserOpen} />
    );
  }
  if (data.shape.identifier === DocShapeId.subscription) {
    const productInstance = new Subscription(data as SubscriptionCrystallizeData, cartStore);
    track(
      productInstance.name,
      productInstance.variants[0]?.sku, // product_id should be sku -> that's ok
      productInstance.variants[0]?.priceVariant?.price,
      productInstance.variants[0]?.stockCount || undefined,
      productInstance.id, // variant_id should be unique product id -> that's ok
      productInstance.variants[0]?.priceVariant?.currency,
      local.language,
    );
    return (
      <NukiIdOverlay
        product={productInstance}
        apiPath={apiPaths.devices.smartHosting.validate}
        isValidId={nukiIdValidation}
        paramName={urlParameterCodes.productDetail.serviceId}
        nukiIds={nukiIds}
        setNukiIds={setNukiIds}
        nukiIdSubtext={productInstance.nukiIdSubtext}
      />
    );
  }
  if (data.shape.identifier === DocShapeId.digitalProduct) {
    const productInstance = new DigitalProduct(data as DigitalProductCrystallizeData, cartStore);
    const productType = productInstance.getProductTypeByPath();
    const validationPath = productInstance.getValidationPath(productType);
    track(
      productInstance.name,
      productInstance.variants[0]?.sku, // product_id should be sku -> that's ok
      productInstance.variants[0]?.priceVariant?.price,
      productInstance.variants[0]?.stockCount || undefined,
      productInstance.id, // variant_id should be unique product id -> that's ok
      productInstance.variants[0]?.priceVariant?.currency,
      local.language,
    );
    return (
      <NukiIdOverlay
        product={productInstance}
        apiPath={validationPath}
        isValidId={nukiIdValidation}
        paramName={urlParameterCodes.productDetail.serviceId}
        nukiIds={nukiIds}
        setNukiIds={setNukiIds}
        nukiIdSubtext={productInstance.nukiIdSubtext}
      />
    );
  }
  return null;
}
